import {
  applyToSearchParams,
  readInt,
  readOneOf,
} from "@/utils/search-params";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import {
  ALL_STATUS_FILTER,
  DEFAULT_PAGE,
  DEFAULT_VOUCHER_PER_PAGE,
  EMPTY_VOUCHER_RANGE,
  SORT_DIRECTIONS,
  STATUS_FILTER_KEYS,
  SortDirection,
  VOUCHER_PARAMS,
  VOUCHER_SORT_COLUMNS,
  VoucherSortColumn,
} from "../constants";
import { VoucherSorting } from "../sorting";
import { DateRange, StatusFilterKey } from "../types";

export interface VoucherListParams {
  range: DateRange;
  statusFilter: StatusFilterKey;
  page: number;
  perPage: number;
  sorting: VoucherSorting;
}

export interface VoucherListParamsActions {
  setRange: (range: DateRange) => void;
  setStatusFilter: (statusFilter: StatusFilterKey) => void;
  setPagination: (page: number, perPage: number) => void;
  setSorting: (sorting: VoucherSorting) => void;
}

export const useVoucherListParams = (): VoucherListParams &
  VoucherListParamsActions => {
  const [searchParams, setSearchParams] = useSearchParams();

  const params: VoucherListParams = useMemo((): VoucherListParams => {
    const sort: VoucherSortColumn | undefined = readOneOf(
      searchParams.get(VOUCHER_PARAMS.SORT),
      VOUCHER_SORT_COLUMNS,
    );
    const direction: SortDirection | undefined = readOneOf(
      searchParams.get(VOUCHER_PARAMS.DIRECTION),
      SORT_DIRECTIONS,
    );

    return {
      range: {
        start:
          searchParams.get(VOUCHER_PARAMS.START) ?? EMPTY_VOUCHER_RANGE.start,
        end: searchParams.get(VOUCHER_PARAMS.END) ?? EMPTY_VOUCHER_RANGE.end,
      },
      statusFilter:
        readOneOf(searchParams.get(VOUCHER_PARAMS.STATUS), STATUS_FILTER_KEYS) ??
        ALL_STATUS_FILTER,
      page: readInt(searchParams.get(VOUCHER_PARAMS.PAGE), DEFAULT_PAGE),
      perPage: readInt(
        searchParams.get(VOUCHER_PARAMS.PER_PAGE),
        DEFAULT_VOUCHER_PER_PAGE,
      ),
      sorting: sort ? { sort, direction } : {},
    };
  }, [searchParams]);

  const update = useCallback(
    (updates: Readonly<Record<string, string | null>>): void => {
      setSearchParams(applyToSearchParams(searchParams, updates));
    },
    [searchParams, setSearchParams],
  );

  const setRange = useCallback(
    (range: DateRange): void => {
      update({
        [VOUCHER_PARAMS.START]: range.start,
        [VOUCHER_PARAMS.END]: range.end,
        [VOUCHER_PARAMS.PAGE]: null,
      });
    },
    [update],
  );

  const setStatusFilter = useCallback(
    (statusFilter: StatusFilterKey): void => {
      update({
        [VOUCHER_PARAMS.STATUS]:
          statusFilter === ALL_STATUS_FILTER ? null : statusFilter,
        [VOUCHER_PARAMS.PAGE]: null,
      });
    },
    [update],
  );

  const setPagination = useCallback(
    (page: number, perPage: number): void => {
      update({
        [VOUCHER_PARAMS.PAGE]: String(page),
        [VOUCHER_PARAMS.PER_PAGE]: String(perPage),
      });
    },
    [update],
  );

  const setSorting = useCallback(
    (sorting: VoucherSorting): void => {
      update({
        [VOUCHER_PARAMS.SORT]: sorting.sort ?? null,
        [VOUCHER_PARAMS.DIRECTION]: sorting.direction ?? null,
        [VOUCHER_PARAMS.PAGE]: null,
      });
    },
    [update],
  );

  return {
    ...params,
    setRange,
    setStatusFilter,
    setPagination,
    setSorting,
  };
};
